import type { Action, Block } from "@cos/shared";
import { renderMarkdown } from "./markdown.js";

export interface BlockHandlers {
  onQuickReply: (value: string) => void;
  onAction: (action: Action) => void;
  /** The shopper's explicit Confirm — the only thing that sends an order. */
  onOrderConfirm?: (orderId: string) => void;
  onFormSubmit?: (fields: Record<string, string>) => void;
}

function money(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export interface RenderBlockOptions {
  /** False for a block on any message but the last — a follow-up two turns back is history, not a live prompt, so its buttons render disabled (widget MessageList). Only the `followup` case reads this; every other block ignores it. */
  interactive?: boolean;
}

/**
 * One renderer per block type, hydrated straight from what the server sent —
 * every field here (title/price/url/image/stock) already went through
 * server-side hydration before it reached the wire (PLAN.md "Rich content
 * is server-authoritative"), so the widget never second-guesses it, only
 * displays it.
 */
export function renderBlock(block: Block, handlers: BlockHandlers, options: RenderBlockOptions = {}): HTMLElement {
  switch (block.type) {
    case "text": {
      const div = document.createElement("div");
      div.className = "cos-block cos-text";
      div.appendChild(renderMarkdown(block.text));
      return div;
    }

    case "notice": {
      const div = document.createElement("div");
      div.className = `cos-block cos-notice cos-notice-${block.level}`;
      div.setAttribute("role", block.level === "error" ? "alert" : "status");
      div.textContent = block.text;
      return div;
    }

    case "links": {
      const div = document.createElement("div");
      div.className = "cos-block cos-links";
      for (const link of block.links) {
        const a = document.createElement("a");
        a.href = link.url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.className = "cos-chip";
        a.textContent = link.label;
        div.appendChild(a);
      }
      return div;
    }

    case "quick_replies": {
      const div = document.createElement("div");
      div.className = "cos-block cos-quick-replies";
      for (const reply of block.replies) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "cos-chip cos-chip-button";
        button.textContent = reply.label;
        button.addEventListener("click", () => handlers.onQuickReply(reply.value));
        div.appendChild(button);
      }
      return div;
    }

    case "actions": {
      const div = document.createElement("div");
      div.className = "cos-block cos-actions";
      for (const action of block.actions) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "cos-chip cos-chip-button";
        button.textContent = action.label;
        if (action.intent === "open_url") {
          button.addEventListener("click", () => window.open(action.url, "_blank", "noopener,noreferrer"));
        } else {
          button.addEventListener("click", () => handlers.onAction(action));
        }
        div.appendChild(button);
      }
      return div;
    }

    case "products": {
      const div = document.createElement("div");
      div.className = `cos-block cos-products cos-products-${block.layout}`;
      for (const item of block.items) {
        const hasLink = Boolean(item.url);
        const card = document.createElement(hasLink ? "a" : "div");
        card.className = "cos-product-card";
        if (hasLink && card instanceof HTMLAnchorElement) {
          card.href = item.url as string;
          card.target = "_blank";
          card.rel = "noopener noreferrer";
        }
        if (item.imageUrl) {
          const img = document.createElement("img");
          img.src = item.imageUrl;
          img.alt = item.title;
          img.loading = "lazy";
          img.className = "cos-product-image";
          card.appendChild(img);
        }
        const body = document.createElement("div");
        body.className = "cos-product-card-body";
        const title = document.createElement("div");
        title.className = "cos-product-title";
        title.textContent = item.title;
        body.appendChild(title);
        if (item.priceCents !== undefined && item.currency) {
          const price = document.createElement("div");
          price.className = "cos-product-price";
          price.textContent = money(item.priceCents, item.currency);
          body.appendChild(price);
        }
        if (item.stock === "out_of_stock") {
          const stock = document.createElement("div");
          stock.className = "cos-product-stock";
          stock.textContent = "Out of stock";
          body.appendChild(stock);
        }
        if (item.note) {
          const note = document.createElement("div");
          note.className = "cos-product-note";
          note.textContent = item.note;
          body.appendChild(note);
        }
        card.appendChild(body);
        div.appendChild(card);
      }
      return div;
    }

    case "order_confirm": {
      const div = document.createElement("div");
      div.className = "cos-block cos-order-confirm";

      const title = document.createElement("div");
      title.className = "cos-order-title";
      title.textContent = "Order summary";
      div.appendChild(title);

      const list = document.createElement("ul");
      list.className = "cos-order-items";
      for (const item of block.items) {
        const li = document.createElement("li");
        li.textContent = `${item.quantity}× ${item.title} — ${money(item.priceCents * item.quantity, block.currency)}`;
        list.appendChild(li);
      }
      div.appendChild(list);

      const total = document.createElement("div");
      total.className = "cos-order-total";
      total.textContent = `Total: ${money(block.totalCents, block.currency)}`;
      div.appendChild(total);

      // Nothing is sent until this is pressed (PLAN.md "Orders never send on
      // the model's say-so"). The button disables itself on click so a
      // double-tap can't produce a second enquiry even before the server's
      // idempotency does.
      const confirm = document.createElement("button");
      confirm.type = "button";
      confirm.className = "cos-chip-button cos-order-confirm-button";
      confirm.textContent = "Confirm";
      confirm.addEventListener("click", () => {
        confirm.disabled = true;
        confirm.textContent = "Sending…";
        handlers.onOrderConfirm?.(block.orderId);
      });
      div.appendChild(confirm);

      return div;
    }

    case "form": {
      const form = document.createElement("form");
      form.className = "cos-block cos-form";
      // Submitted fields go straight to `visitor_facts` server-side, with no
      // model in the path: a value typed into a box labelled "Email" needs
      // no extraction, and routing it through one would be a way to lose it.
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        const fields: Record<string, string> = {};
        for (const [name, value] of new FormData(form).entries()) {
          if (typeof value === "string" && value.trim().length > 0) fields[name] = value.trim();
        }
        handlers.onFormSubmit?.(fields);
        form.querySelectorAll("input, textarea, button").forEach((element) => {
          (element as HTMLInputElement).disabled = true;
        });
      });

      for (const field of block.fields) {
        const label = document.createElement("label");
        label.className = "cos-form-field";
        const labelText = document.createElement("span");
        labelText.textContent = field.label;
        label.appendChild(labelText);

        const input = field.kind === "textarea" ? document.createElement("textarea") : document.createElement("input");
        if (input instanceof HTMLInputElement) {
          input.type = field.kind === "textarea" ? "text" : field.kind;
        }
        input.name = field.name;
        if (field.required) input.required = true;
        label.appendChild(input);
        form.appendChild(label);
      }

      const submit = document.createElement("button");
      submit.type = "submit";
      submit.className = "cos-chip-button";
      submit.textContent = block.submitLabel ?? "Submit";
      form.appendChild(submit);

      return form;
    }

    case "followup": {
      const interactive = options.interactive ?? true;
      const div = document.createElement("div");
      const hasDescriptions = block.options.some((option) => option.description);
      div.className = `cos-block cos-followup${hasDescriptions ? "" : " cos-followup-options-inline"}`;
      div.setAttribute("role", "group");

      const question = document.createElement("div");
      question.className = "cos-followup-question";
      question.textContent = block.question;
      div.appendChild(question);

      const optionsList = document.createElement("div");
      optionsList.className = "cos-followup-options";

      function answer(value: string): void {
        if (div.hasAttribute("data-answered")) return;
        div.setAttribute("data-answered", "true");
        optionsList.querySelectorAll("button").forEach((button) => {
          (button as HTMLButtonElement).disabled = true;
        });
        handlers.onQuickReply(value);
      }

      for (const option of block.options) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = hasDescriptions ? "cos-followup-option" : "cos-chip-button";
        button.disabled = !interactive;

        const label = document.createElement("span");
        label.className = "cos-followup-option-label";
        label.textContent = option.label;
        button.appendChild(label);

        if (option.description) {
          const description = document.createElement("span");
          description.className = "cos-followup-option-description";
          description.textContent = option.description;
          button.appendChild(description);
        }

        button.addEventListener("click", () => answer(option.value));
        optionsList.appendChild(button);
      }
      div.appendChild(optionsList);

      if (interactive) {
        const hint = document.createElement("div");
        hint.className = "cos-followup-hint";
        hint.textContent = "…or type your own answer below";
        div.appendChild(hint);
      }

      return div;
    }
  }
}
